import { Fragment, useEffect, useState } from "react";
import { api } from "../lib/api";

/** Nivel de servicio (contrato con Italfarmaco, puntos 14 y 15): disponibilidad mensual medida desde
 *  fuera, mantenimientos programados y registro de incidencias con sus plazos en horario de servicio
 *  (9:00-18:00 laborables, festivos de la sede). Solo superadmin; las páginas del superadmin van en español. */

type Mw = { id: string; starts_at: string; ends_at: string; description: string; announced_at: string; descuenta: boolean; weekly: boolean };
type Avail = { medido: boolean; minutos: number; caidos: number; mantenimiento: number; porcentaje: number | null;
  desde: string | null; interrupciones: { desde: string; hasta: string; minutos: number }[]; mantenimientos: Mw[]; objetivo: number };
type Fase = { fase: string; nombre: string; limite: string | null; real: string | null; estado: string };
type Pausa = { desde: string; hasta: string | null; motivo: "espera_cliente" | "fuente_externa" };
type Inc = { id: string; title: string; description: string | null; severity: string; origin: string; organization_id: string | null;
  received_at: string; pauses: Pausa[]; diagnosis: string | null; notes: string | null; fases: Fase[] };
type Org = { id: string; name: string };
type Problema = { fuente: string; problema: string };
type ContractCheck = { dia: string; pasada: Problema[]; frecuencias: Problema[] };

const card: React.CSSProperties = { background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
  padding: "1rem 1.25rem", marginBottom: 16 };
const inp: React.CSSProperties = { background: "var(--clr-input-bg)", border: "1px solid var(--clr-border)", borderRadius: 10,
  color: "var(--clr-text)", padding: "6px 10px", fontSize: "0.82rem" };
const btn = (primary = true): React.CSSProperties => ({ background: primary ? "var(--clr-org-primary)" : "transparent",
  color: primary ? "#fff" : "var(--clr-org-primary)", border: primary ? 0 : "1px solid var(--clr-org-primary)", borderRadius: 999,
  padding: "6px 16px", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" });
const ESTADO: Record<string, [string, string]> = {
  cumplido: ["En plazo", "#16a34a"], incumplido: ["Fuera de plazo", "#dc2626"], fuera_de_plazo: ["Vencido", "#dc2626"],
  en_plazo: ["Abierto, en plazo", "#d97706"], sin_plazo: ["Sin plazo", "#94a3b8"],
};
const SEV: Record<string, string> = { critica: "Crítica", alta: "Alta", media: "Media (normal)", baja: "Baja / evolutiva" };
/** Tabla de severidades del anexo de SLA del contrato, tal cual (Carlos, 23 oct 2026). */
const SEV_CRITERIO: Record<string, string> = {
  critica: "Plataforma inaccesible de forma generalizada, fallo general de autenticación, imposibilidad de consultar datos ya procesados o interrupción general de la monitorización.",
  alta: "Funcionalidad principal degradada, error que afecta a varios usuarios, fallo de una funcionalidad del alcance o retraso significativo en la actualización de una fuente diaria.",
  media: "Sin plazo pactado.",
  baja: "Sin plazo pactado.",
};
const hm = (iso: string) => new Date(iso).toLocaleTimeString("es-ES", { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" });
const weekday = (iso: string) => new Date(iso).toLocaleDateString("es-ES", { timeZone: "Europe/Madrid", weekday: "long" });

const local = (iso: string | null) => iso ? new Date(iso).toLocaleString("es-ES", { timeZone: "Europe/Madrid",
  day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
/** <input type="datetime-local"> trabaja en hora local del navegador. */
const toInput = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromInput = (v: string) => (v ? new Date(v).toISOString() : null);
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function SuperAdminServicioPage() {
  const [month, setMonth] = useState(thisMonth());
  const [avail, setAvail] = useState<Avail | null>(null);
  const [mws, setMws] = useState<Mw[]>([]);
  const [incs, setIncs] = useState<Inc[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [editing, setEditing] = useState<Inc | "new" | null>(null);
  const [err, setErr] = useState("");
  const [check, setCheck] = useState<ContractCheck | null>(null);
  useEffect(() => { api.get<ContractCheck>("/sla/contract-check").then(setCheck).catch(() => {}); }, []);

  const load = () => {
    api.get<Avail>(`/sla/availability?month=${month}`).then(setAvail).catch(e => setErr(String(e?.message ?? e)));
    api.get<Mw[]>("/sla/maintenance").then(setMws).catch(() => {});
    api.get<Inc[]>(`/sla/incidents?month=${month}`).then(setIncs).catch(() => {});
  };
  useEffect(load, [month]);
  useEffect(() => { api.get<Org[]>("/organizations").then(setOrgs).catch(() => {}); }, []);

  const [mw, setMw] = useState({ starts_at: "", ends_at: "", description: "", weekly: false });
  const addMw = async () => {
    setErr("");
    try {
      await api.post("/sla/maintenance", { starts_at: fromInput(mw.starts_at), ends_at: fromInput(mw.ends_at), description: mw.description, weekly: mw.weekly });
      setMw({ starts_at: "", ends_at: "", description: "", weekly: false }); load();
    } catch (e: unknown) { setErr((e as Error)?.message ?? "Error"); }
  };
  const delMw = async (id: string) => { await api.delete(`/sla/maintenance/${id}`); load(); };

  return (
    <div style={{ padding: "1.75rem 2rem", maxWidth: 1100, margin: "0 auto", color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, flex: 1 }}>Nivel de servicio</h1>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={inp} aria-label="Mes" />
      </div>
      {err && <div role="alert" style={{ color: "#dc2626", fontSize: "0.82rem", marginBottom: 10 }}>{err}</div>}

      {/* ── Fuentes del contrato hoy (vigilante diario de las 9:50) ── */}
      {check && (
        <div style={{ ...card, borderColor: check.pasada.length + check.frecuencias.length ? "#d9770688" : "var(--clr-border)" }}>
          <strong>Fuentes del contrato hoy</strong>
          <span style={{ marginLeft: 8, fontSize: "0.78rem", color: "var(--clr-text3)" }}>
            pasada de las 9:00 y frecuencia mínima pactada · si algo falla, a las 9:50 sale el aviso por correo y se abre una incidencia Alta
          </span>
          {check.pasada.length + check.frecuencias.length === 0 ? (
            <p style={{ margin: "8px 0 0", color: "#16a34a", fontSize: "0.85rem", fontWeight: 600 }}>Todo en orden.</p>
          ) : (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: "0.82rem", lineHeight: 1.5 }}>
              {[...check.pasada.map(p => ({ ...p, tipo: "Pasada de las 9:00" })), ...check.frecuencias.map(p => ({ ...p, tipo: "Frecuencia" }))]
                .map((p, i) => <li key={i}><strong>{p.fuente}</strong> · {p.tipo}: {p.problema}</li>)}
            </ul>
          )}
        </div>
      )}

      {/* ── Disponibilidad ── */}
      <div style={card}>
        <strong>Disponibilidad del mes</strong>
        {!avail ? <p style={{ color: "var(--clr-text4)" }}>Cargando…</p> : !avail.medido ? (
          <p style={{ color: "var(--clr-text3)", fontSize: "0.85rem" }}>Sin medición para este mes (el vigilante externo escribe en el servidor de producción).</p>
        ) : (
          <>
            <div style={{ display: "flex", gap: 28, margin: "10px 0", flexWrap: "wrap" }}>
              <div><div style={{ fontSize: "1.8rem", fontWeight: 800, color: (avail.porcentaje ?? 0) >= avail.objetivo ? "#16a34a" : "#dc2626" }}>
                {avail.porcentaje?.toFixed(2).replace(".", ",")} %</div><div style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>objetivo {avail.objetivo} %</div></div>
              <div><div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{avail.caidos}</div><div style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>minutos sin servicio de {avail.minutos.toLocaleString("es-ES")}</div></div>
              <div><div style={{ fontSize: "1.2rem", fontWeight: 700 }}>{avail.mantenimiento}</div><div style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>minutos de mantenimiento descontados</div></div>
            </div>
            {avail.interrupciones.length > 0 && (
              <table style={{ borderCollapse: "collapse", fontSize: "0.8rem" }}>
                <thead><tr><th style={{ textAlign: "left", padding: "4px 12px 4px 0" }}>Desde</th><th style={{ textAlign: "left", padding: "4px 12px" }}>Hasta</th><th style={{ padding: "4px 12px" }}>Minutos</th></tr></thead>
                <tbody>{avail.interrupciones.slice(0, 50).map(x => (
                  <tr key={x.desde}><td style={{ padding: "3px 12px 3px 0" }}>{local(x.desde)}</td><td style={{ padding: "3px 12px" }}>{local(x.hasta)}</td><td style={{ padding: "3px 12px", textAlign: "center" }}>{x.minutos}</td></tr>
                ))}</tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* ── Mantenimientos ── */}
      <div style={card}>
        <strong>Mantenimientos programados</strong>
        <p style={{ margin: "4px 0 10px", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
          Solo se descuentan de la disponibilidad si se comunican a Italfarmaco con 24 horas de antelación (fecha de comunicación = al guardarlo).
          La ventana fija (martes de 22:00 a 23:00) se registra una vez, con la primera fecha y «Cada semana», el día que se comunique por escrito.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <input type="datetime-local" value={mw.starts_at} onChange={e => setMw(m => ({ ...m, starts_at: e.target.value }))} style={inp} aria-label="Inicio" />
          <input type="datetime-local" value={mw.ends_at} onChange={e => setMw(m => ({ ...m, ends_at: e.target.value }))} style={inp} aria-label="Fin" />
          <input placeholder="Descripción" value={mw.description} onChange={e => setMw(m => ({ ...m, description: e.target.value }))} style={{ ...inp, minWidth: 240 }} />
          <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: "0.8rem" }}>
            <input type="checkbox" checked={mw.weekly} onChange={e => setMw(m => ({ ...m, weekly: e.target.checked }))} /> Cada semana
          </label>
          <button style={btn()} onClick={addMw} disabled={!mw.starts_at || !mw.ends_at || mw.description.length < 3}>Añadir</button>
        </div>
        {mws.map(w => (
          <div key={w.id} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: "0.82rem", padding: "4px 0", borderTop: "1px solid var(--clr-border2)" }}>
            <span style={{ flex: 1 }}>{w.weekly
              ? <>Cada {weekday(w.starts_at)} de {hm(w.starts_at)} a {hm(w.ends_at)}, desde el {local(w.starts_at).slice(0, 8)} · {w.description}</>
              : <>{local(w.starts_at)} → {local(w.ends_at)} · {w.description}</>}</span>
            <span style={{ color: w.descuenta ? "#16a34a" : "#d97706", fontSize: "0.75rem" }}>{w.weekly ? `Ventana fija, comunicada ${local(w.announced_at)}` : w.descuenta ? "Comunicado con 24 h" : `Comunicado ${local(w.announced_at)}: no descuenta`}</span>
            <button style={{ ...btn(false), padding: "2px 10px" }} onClick={() => delMw(w.id)}>Quitar</button>
          </div>
        ))}
      </div>

      {/* ── Incidencias ── */}
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <strong style={{ flex: 1 }}>Incidencias del mes</strong>
          <button style={btn()} onClick={() => setEditing("new")}>Nueva incidencia</button>
        </div>
        <p style={{ margin: "4px 0 10px", fontSize: "0.78rem", color: "var(--clr-text3)" }}>
          Plazos en horario de servicio (9:00-18:00, laborables, festivos de Madrid). Crítica: respuesta 4 h, diagnóstico 8 h, solución provisional 1 día, resolución 5 días. Alta: 8 h, 16 h, 3 y 10 días.
        </p>
        <dl style={{ margin: "0 0 10px", fontSize: "0.76rem", color: "var(--clr-text3)", display: "grid", gridTemplateColumns: "max-content 1fr", gap: "2px 10px" }}>
          {Object.entries(SEV_CRITERIO).map(([k, v]) => (
            <Fragment key={k}><dt style={{ fontWeight: 700 }}>{SEV[k]}</dt><dd style={{ margin: 0 }}>{v}</dd></Fragment>
          ))}
        </dl>
        {incs.length === 0 && <p style={{ color: "var(--clr-text4)", fontSize: "0.85rem" }}>Sin incidencias este mes.</p>}
        {incs.map(i => (
          <div key={i.id} style={{ borderTop: "1px solid var(--clr-border2)", padding: "8px 0" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
              <strong style={{ fontSize: "0.88rem" }}>{i.title}</strong>
              <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>{SEV[i.severity]} · {i.origin === "cliente" ? "comunicada por el cliente" : "detectada por la monitorización"} · recibida {local(i.received_at)}</span>
              <span style={{ flex: 1 }} />
              <button style={{ ...btn(false), padding: "2px 10px" }} onClick={() => setEditing(i)}>Editar</button>
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
              {i.fases.map(f => (
                <span key={f.fase} style={{ fontSize: "0.75rem" }}>
                  {f.nombre}: <span style={{ color: ESTADO[f.estado][1], fontWeight: 600 }}>{ESTADO[f.estado][0]}</span>
                  {f.limite && <span style={{ color: "var(--clr-text4)" }}> (límite {local(f.limite)}{f.real ? `, hecho ${local(f.real)}` : ""})</span>}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && <IncidentForm inc={editing === "new" ? null : editing} orgs={orgs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function IncidentForm({ inc, orgs, onClose, onSaved }: { inc: Inc | null; orgs: Org[]; onClose: () => void; onSaved: () => void }) {
  const real = (f: string) => inc?.fases.find(x => x.fase === f)?.real ?? null;
  const [f, setF] = useState({
    title: inc?.title ?? "", description: inc?.description ?? "", severity: inc?.severity ?? "alta",
    origin: inc?.origin ?? "cliente", organization_id: inc?.organization_id ?? "",
    received_at: toInput(inc?.received_at ?? new Date().toISOString()),
    first_response_at: toInput(real("first_response_at")), diagnosis_at: toInput(real("diagnosis_at")),
    workaround_at: toInput(real("workaround_at")), resolved_at: toInput(real("resolved_at")),
    diagnosis: inc?.diagnosis ?? "", notes: inc?.notes ?? "",
  });
  const [pauses, setPauses] = useState<Pausa[]>(inc?.pauses ?? []);
  const [err, setErr] = useState("");
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF(x => ({ ...x, [k]: e.target.value }));
  const now = (k: keyof typeof f) => setF(x => ({ ...x, [k]: toInput(new Date().toISOString()) }));
  const save = async () => {
    setErr("");
    const body = { ...f, organization_id: f.organization_id || null, received_at: fromInput(f.received_at),
      first_response_at: fromInput(f.first_response_at), diagnosis_at: fromInput(f.diagnosis_at),
      workaround_at: fromInput(f.workaround_at), resolved_at: fromInput(f.resolved_at),
      description: f.description || null, diagnosis: f.diagnosis || null, notes: f.notes || null, pauses };
    try {
      if (inc) await api.put(`/sla/incidents/${inc.id}`, body); else await api.post("/sla/incidents", body);
      onSaved();
    } catch (e: unknown) { setErr((e as Error)?.message ?? "Error"); }
  };
  const fase = (k: "first_response_at" | "diagnosis_at" | "workaround_at" | "resolved_at", label: string) => (
    <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.8rem" }}>
      <span style={{ width: 150 }}>{label}</span>
      <input type="datetime-local" value={f[k]} onChange={set(k)} style={inp} />
      <button type="button" style={{ ...btn(false), padding: "2px 10px" }} onClick={() => now(k)}>Ahora</button>
    </label>
  );
  return (
    <div role="dialog" aria-modal="true" onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(11,18,21,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.5rem", width: 640,
                    maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: "1.05rem" }}>{inc ? "Editar incidencia" : "Nueva incidencia"}</h2>
        <input placeholder="Título" value={f.title} onChange={set("title")} style={inp} />
        <textarea placeholder="Descripción" value={f.description} onChange={set("description")} style={{ ...inp, minHeight: 60 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={f.severity} onChange={set("severity")} style={inp} aria-label="Severidad" title={SEV_CRITERIO[f.severity]}>
            {Object.entries(SEV).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={f.origin} onChange={set("origin")} style={inp} aria-label="Origen">
            <option value="cliente">Comunicada por el cliente</option><option value="monitorizacion">Detectada por la monitorización</option>
          </select>
          <select value={f.organization_id} onChange={set("organization_id")} style={inp} aria-label="Organización">
            <option value="">Toda la plataforma</option>{orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div style={{ fontSize: "0.76rem", color: "var(--clr-text3)" }}>{SEV[f.severity]}: {SEV_CRITERIO[f.severity]}</div>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.8rem" }}>
          <span style={{ width: 150 }}>Recibida</span><input type="datetime-local" value={f.received_at} onChange={set("received_at")} style={inp} />
        </label>
        {fase("first_response_at", "Primera respuesta")}
        {fase("diagnosis_at", "Diagnóstico preliminar")}
        <textarea placeholder="Diagnóstico: causa probable, alcance, pérdida de datos" value={f.diagnosis} onChange={set("diagnosis")} style={{ ...inp, minHeight: 50 }} />
        {fase("workaround_at", "Solución provisional")}
        {fase("resolved_at", "Resolución")}
        <div style={{ fontSize: "0.8rem", fontWeight: 600, marginTop: 4 }}>Cómputo suspendido</div>
        {pauses.map((p, n) => (
          <div key={n} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <input type="datetime-local" value={toInput(p.desde)} onChange={e => setPauses(ps => ps.map((x, i) => i === n ? { ...x, desde: fromInput(e.target.value) ?? x.desde } : x))} style={inp} aria-label="Desde" />
            <input type="datetime-local" value={toInput(p.hasta)} onChange={e => setPauses(ps => ps.map((x, i) => i === n ? { ...x, hasta: fromInput(e.target.value) } : x))} style={inp} aria-label="Hasta" />
            <select value={p.motivo} onChange={e => setPauses(ps => ps.map((x, i) => i === n ? { ...x, motivo: e.target.value as Pausa["motivo"] } : x))} style={inp} aria-label="Motivo">
              <option value="espera_cliente">A la espera del cliente</option><option value="fuente_externa">Causa en la fuente externa</option>
            </select>
            <button type="button" style={{ ...btn(false), padding: "2px 10px" }} onClick={() => setPauses(ps => ps.filter((_, i) => i !== n))}>Quitar</button>
          </div>
        ))}
        <button type="button" style={{ ...btn(false), alignSelf: "flex-start" }}
          onClick={() => setPauses(ps => [...ps, { desde: new Date().toISOString(), hasta: null, motivo: "espera_cliente" }])}>Añadir pausa</button>
        <textarea placeholder="Notas" value={f.notes} onChange={set("notes")} style={{ ...inp, minHeight: 40 }} />
        {err && <div role="alert" style={{ color: "#dc2626", fontSize: "0.8rem" }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={btn(false)} onClick={onClose}>Cancelar</button>
          <button style={btn()} onClick={save} disabled={f.title.trim().length < 3}>Guardar</button>
        </div>
      </div>
    </div>
  );
}
